import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Leaf, Shield, Sparkles, BookOpen, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

// Video and poster URLs
const VIDEO_URL = "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/logos-fondos/fondos/Realistic_cinematic_environmental_20260103010.mp4";
const POSTER_URL = "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/logos-fondos/fondos/hero-poster.jpg";

const Hero = () => {
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  // Detect if device prefers reduced motion or is mobile
  const prefersReducedMotion = typeof window !== 'undefined' && 
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  // Use Intersection Observer to lazy load video on desktop
  useEffect(() => {
    // Skip video loading on mobile or if user prefers reduced motion
    const isMobile = window.innerWidth < 768;
    if (isMobile || prefersReducedMotion) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShouldLoadVideo(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [prefersReducedMotion]);

  // Handle video loaded state
  const handleVideoLoaded = () => {
    setIsVideoLoaded(true);
  };

  return (
    <section 
      ref={sectionRef}
      id="inicio" 
      className="relative min-h-[100svh] sm:min-h-screen flex items-center pt-16 sm:pt-20 overflow-hidden"
    >
      {/* Static poster background - shows immediately */}
      <div 
        className="absolute inset-0 w-full h-full z-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${POSTER_URL})` }}
        aria-hidden="true"
      />

      {/* Video background - lazy loaded on desktop only */}
      {shouldLoadVideo && (
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          onLoadedData={handleVideoLoaded}
          className={`absolute inset-0 w-full h-full object-cover z-0 transition-opacity duration-700 ${
            isVideoLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          aria-hidden="true"
        >
          <source src={VIDEO_URL} type="video/mp4" />
        </video>
      )}

      {/* Overlay para legibilidad */}
      <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/60 to-background/80 z-[1]"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          {/* Badge with leaf accent */}
          <div className="inline-flex items-center gap-2 bg-accent/80 border border-primary/15 rounded-full px-4 py-2.5 sm:px-6 sm:py-3.5 mt-6 sm:mt-10 mb-8 sm:mb-14 shadow-nature">
            <Leaf className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            <span className="text-primary text-xs sm:text-sm font-semibold">
              Soluciones Sustentables de Control de Plagas
            </span>
          </div>

          {/* Main Title */}
          <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black text-foreground mb-6 sm:mb-10 leading-[1.1] tracking-tight px-2">
            Innovación y Soluciones para el{" "}
            <span className="text-primary">Control de Plagas</span>
          </h1>

          {/* Subtitle */}
          <p className="text-base sm:text-xl md:text-2xl text-muted-foreground mb-8 sm:mb-14 max-w-3xl mx-auto leading-relaxed font-light px-4">
            Estamos comprometidos con el medio ambiente y con los nuevos tiempos que demandan mayor sustentabilidad y un compromiso social con la industria.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-5 justify-center items-center mb-6 sm:mb-8 px-4">
            <Button size="lg" variant="primary" className="text-base sm:text-lg group w-full sm:w-auto sm:min-w-[240px] shadow-nature" asChild>
              <Link to="/productos">
                Ver Nuestras Soluciones
                <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="text-base sm:text-lg w-full sm:w-auto sm:min-w-[240px]" asChild>
              <Link to="/cotizacion">
                Solicitar Cotización
              </Link>
            </Button>
          </div>

          {/* Catalog Button - Secondary CTA */}
          <div className="mb-12 sm:mb-24">
            <Button size="lg" variant="secondary" className="text-base sm:text-lg group w-full sm:w-auto sm:min-w-[240px]" asChild>
              <a
                href="https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/catalogo//CAT_ECONTROL_MEX_2025.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                <BookOpen className="w-4 h-4 sm:w-5 sm:h-5" />
                Ver Catálogo 2025
                <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
              </a>
            </Button>
          </div>

          {/* Stats - Clean eco cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 max-w-4xl mx-auto px-2">
            <div className="bg-card rounded-2xl sm:rounded-3xl p-6 sm:p-10 border border-border/60 shadow-nature hover:shadow-hover transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-accent rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-5">
                <Shield className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
              </div>
              <div className="text-3xl sm:text-5xl font-black text-primary mb-2 sm:mb-3">30+</div>
              <div className="text-xs sm:text-sm text-muted-foreground font-medium">Años de Experiencia</div>
            </div>
            <div className="bg-card rounded-2xl sm:rounded-3xl p-6 sm:p-10 border border-border/60 shadow-nature hover:shadow-hover transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-accent rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-5">
                <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
              </div>
              <div className="text-3xl sm:text-5xl font-black text-primary mb-2 sm:mb-3">500+</div>
              <div className="text-xs sm:text-sm text-muted-foreground font-medium">Clientes Satisfechos</div>
            </div>
            <div className="bg-card rounded-2xl sm:rounded-3xl p-6 sm:p-10 border border-border/60 shadow-nature hover:shadow-hover transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-accent rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-5">
                <Leaf className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
              </div>
              <div className="text-3xl sm:text-5xl font-black text-primary mb-2 sm:mb-3">100%</div>
              <div className="text-xs sm:text-sm text-muted-foreground font-medium">Eco-Friendly</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom wave decoration */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="hsl(var(--background))"/>
        </svg>
      </div>

      {/* Nature pattern below video - matches AboutUs style */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-nature-pattern opacity-25 pointer-events-none"></div>
    </section>
  );
};

export default Hero;
